import { Button, Chip, Tabs, Tab } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const PDFSummarizer: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textInput, setTextInput] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [selectedType, setSelectedType] = useState("brief");
  const [activeTab, setActiveTab] = useState("file");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const summaryTypes = [
    { id: "brief", label: "Brief Summary", icon: "lucide:file-text" },
    { id: "study", label: "Study Guide", icon: "lucide:book-open" },
    { id: "takeaways", label: "Key Takeaways", icon: "lucide:lightbulb" },
    { id: "meeting", label: "Meeting Minutes", icon: "lucide:users" },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    }
  };

  const handleSummarize = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2000);
  };

  const canSubmit = () => {
    if (activeTab === "file") return selectedFile !== null;
    if (activeTab === "text") return textInput.trim().length > 0;
    if (activeTab === "url") return urlInput.trim().length > 0;
    return false;
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "PDF Summarizer - YouWo AI",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Summarize books, reports, academic papers, and PDFs in seconds using AI. Free PDF summary generator.",
  };

  const howItWorksSteps = [
    {
      title: "Upload Your PDF",
      description: "Drag and drop your PDF file or click to browse. Supports files up to 100MB.",
    },
    {
      title: "AI Extracts Key Points",
      description: "Our AI reads and analyzes your document, identifying main ideas and important details.",
    },
    {
      title: "Get Instant Summary",
      description: "Receive a clear, organized summary with key takeaways and insights.",
    },
  ];

  const benefits = [
    {
      title: "100+ Languages",
      description: "Summarize PDFs in over 100 languages with automatic language detection.",
      icon: "lucide:globe",
    },
    {
      title: "Page References",
      description: "Each summary point includes page numbers so you can jump back to the source.",
      icon: "lucide:bookmark",
    },
    {
      title: "100% Secure",
      description: "Your files are processed securely and automatically deleted after summarization.",
      icon: "lucide:shield-check",
    },
    {
      title: "Multiple Summary Styles",
      description: "Choose from brief summaries, study guides, key takeaways, or meeting minutes format.",
      icon: "lucide:layout-list",
    },
    {
      title: "Chat with Your PDF",
      description: "Ask follow-up questions about your document with AI-powered chat.",
      icon: "lucide:message-circle",
    },
    {
      title: "Export with Pro",
      description: "Export summaries to various formats and save to your library with Pro.",
      icon: "lucide:download",
    },
  ];

  const faqs = [
    {
      question: "What is the PDF Summarizer?",
      answer: "The PDF Summarizer is a free AI tool that transforms lengthy PDF documents into concise, easy-to-read summaries. It works with textbooks, research papers, reports, and any PDF file.",
    },
    {
      question: "Is my data safe?",
      answer: "Absolutely. Your files are encrypted during upload, processed on secure servers, and automatically deleted after summarization. We never store or share your documents.",
    },
    {
      question: "What types of PDFs can I summarize?",
      answer: "You can summarize any PDF including academic papers, research documents, business reports, legal contracts, textbooks, and more. The tool works with both text-based and scanned PDFs.",
    },
    {
      question: "Do I need to sign up to use it?",
      answer: "No! You can use the PDF Summarizer without signing up. Create a free account to save unlimited summaries and access additional features like PDF chat.",
    },
    {
      question: "Can I summarize scanned PDFs?",
      answer: "Yes! Our AI includes OCR (Optical Character Recognition) technology that can extract and summarize text from scanned documents and images within PDFs.",
    },
    {
      question: "Is there a file size limit?",
      answer: "Free users can upload PDFs up to 10MB. Sign up for a free account to upload files up to 100MB and summarize longer documents.",
    },
  ];

  const relatedTools = [
    {
      name: "YouTube Summarizer",
      description: "Transform YouTube videos into concise summaries with AI-generated timestamps.",
      href: "/tools/youtube-summarizer",
      icon: "lucide:youtube",
      iconColor: "text-red-500",
    },
    {
      name: "AI Audio Transcriber",
      description: "Convert any audio file to accurate text with speaker detection.",
      href: "/tools/audio-transcriber",
      icon: "lucide:mic",
      iconColor: "text-blue-500",
    },
    {
      name: "AI Flashcard Maker",
      description: "Generate study flashcards automatically from any content.",
      href: "/tools/flashcard-maker",
      icon: "lucide:layers",
      iconColor: "text-purple-500",
    },
    {
      name: "PDF to Study Guide",
      description: "Turn PDFs into source-cited study guides, quizzes, and flashcards.",
      href: "/tools/pdf-to-study-guide",
      icon: "lucide:book-open-check",
      iconColor: "text-cornflowerblue",
    },
    {
      name: "AI Quiz Generator",
      description: "Create practice quizzes to test your understanding of any topic.",
      href: "/tools/quiz-generator",
      icon: "lucide:help-circle",
      iconColor: "text-green-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free PDF Summarizer - AI Document Summary Generator | YouWo AI"
        metaDescription="Summarize books, research papers, reports, and lengthy PDFs in seconds using AI. Free PDF summary generator with key insights extraction. No sign-up required."
        keywords="pdf summarizer, document summarizer, ai summary, pdf reader, research paper summary, book summary, free pdf tool"
        canonicalUrl="https://youwo.ai/tools/pdf-summarizer"
        structuredData={structuredData}
        heroTitle="PDF Summarizer"
        heroSubtitle="Summarize books, reports, and PDFs in seconds with page references. Supports 100+ languages. Get key insights without hours of reading."
        heroIcon="lucide:file-text"
        heroIconColor="text-orange-500"
        heroIconBg="bg-orange-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI PDF Summarizer?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* Input Type Tabs */}
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as string)}
            variant="bordered"
            classNames={{
              tabList: "gap-4 w-full mb-6",
              tab: "px-6 py-3",
              cursor: "bg-cornflowerblue",
            }}
          >
            <Tab
              key="file"
              title={
                <div className="flex items-center gap-2">
                  <Icon icon="lucide:upload" />
                  <span>File</span>
                </div>
              }
            />
            <Tab
              key="text"
              title={
                <div className="flex items-center gap-2">
                  <Icon icon="lucide:text" />
                  <span>Text</span>
                </div>
              }
            />
            <Tab
              key="url"
              title={
                <div className="flex items-center gap-2">
                  <Icon icon="lucide:link" />
                  <span>URL</span>
                </div>
              }
            />
          </Tabs>

          {/* File Upload Area */}
          {activeTab === "file" && (
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isDragging
                  ? "border-cornflowerblue bg-blue-50"
                  : selectedFile
                  ? "border-green-400 bg-green-50"
                  : "border-gray-200 hover:border-cornflowerblue"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf"
                onChange={handleFileSelect}
              />

              {selectedFile ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-green-100 flex items-center justify-center">
                    <Icon icon="lucide:file-check" className="text-green-600 text-3xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    Remove & choose another
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Icon icon="lucide:upload-cloud" className="text-gray-400 text-3xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      Drag and drop your PDF here
                    </p>
                    <p className="text-sm text-gray-500">
                      or click to browse • Max file size: 10MB
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Text Input Area */}
          {activeTab === "text" && (
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste your text here to summarize..."
              className="w-full h-48 p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-cornflowerblue"
            />
          )}

          {/* URL Input Area */}
          {activeTab === "url" && (
            <div className="flex gap-3">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/document.pdf"
                className="flex-1 h-14 px-4 border border-gray-200 rounded-xl focus:outline-none focus:border-cornflowerblue"
              />
            </div>
          )}

          {/* Summary Type Selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-6">
            <span className="text-sm text-gray-600 font-medium">Summary Type:</span>
            <div className="flex flex-wrap gap-2">
              {summaryTypes.map((type) => (
                <Chip
                  key={type.id}
                  variant={selectedType === type.id ? "solid" : "bordered"}
                  color={selectedType === type.id ? "primary" : "default"}
                  className={`cursor-pointer transition-all ${
                    selectedType === type.id
                      ? "bg-cornflowerblue text-white"
                      : "border-gray-200 text-gray-600 hover:border-cornflowerblue"
                  }`}
                  startContent={<Icon icon={type.icon} className="text-sm" />}
                  onClick={() => setSelectedType(type.id)}
                >
                  {type.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <Button
              color="primary"
              size="lg"
              className="w-full bg-cornflowerblue h-14 font-semibold"
              isLoading={isLoading}
              isDisabled={!canSubmit()}
              onClick={handleSummarize}
            >
              {isLoading ? "Summarizing..." : "Generate Summary"}
            </Button>
          </div>
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="PDF Summary"
      />
    </>
  );
};

export default PDFSummarizer;
